import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Radio,
  CircularProgress,
  Select,
  MenuItem,
  Box,
  TablePagination,
} from "@mui/material";
import { ArrowDropUp, ArrowDropDown, FilterList } from "@mui/icons-material";
import { format } from "date-fns";
import {
  AccelContext,
  useAccelContext,
} from "../../contexts/AccelContext/AccelContext";
import { Accel } from "../../api/accel";
import useSWR from "swr";
import AccelBarChart from "../AccelBarChart/AccelBarChart";

interface SuggestionBarProps {
  showUncategorized: boolean;
  setShowUncategorized: React.Dispatch<React.SetStateAction<boolean>>;
  mutate: () => void;
}

const SuggestionBar: React.FC<SuggestionBarProps> = ({
  showUncategorized,
  setShowUncategorized,
  mutate,
}) => {
  const { theme, companyId, auth, apiUrl } = useAccelContext();
  const [isLoading, setIsLoading] = useState(false);

  const handleAutoCategorize = async () => {
    setIsLoading(true);
    try {
      await Accel.reconcileTransactions(apiUrl, auth.access_token, {
        body: { company_id: companyId, categorizationStatus: "PENDING" },
      });
      mutate();
    } finally {
      setIsLoading(false);
      mutate();
      window.location.reload();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: theme?.accent?.hex || "#000000",
        padding: "8px 16px",
        borderRadius: "4px 4px 0 0",
        marginBottom: "0px",
      }}
    >
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<FilterList />}
          sx={{
            borderColor: theme?.text?.hex || "#000000",
            borderRadius: "20px",
            color: theme?.text?.hex || "#000000",
            textTransform: "none",
            backgroundColor: showUncategorized
              ? `${theme?.primary?.hex || "#000000"}33`
              : "transparent",
          }}
          onClick={() => setShowUncategorized(!showUncategorized)}
        >
          Uncategorized
        </Button>
      </Box>
    </Box>
  );
};

const getCategoryColor = (type: string) => {
  switch (type) {
    case "ASSET":
      return "#4CAF50";
    case "LIABILITY":
      return "#F44336";
    case "EQUITY":
      return "#2196F3";
    case "REVENUE":
      return "#FFC107";
    case "EXPENSE":
      return "#FF5722";
    case "NONE":
      return "#9E9E9E";
    default:
      return "#FFA500";
  }
};

const CATEGORY_TYPES = [
  "ASSET",
  "LIABILITY",
  "EQUITY",
  "REVENUE",
  "EXPENSE",
  "INTEREST_INCOME",
  "COGS_EXPENSE",
  "SGA_EXPENSE",
  "SM_EXPENSE",
  "RD_EXPENSE",
  "INTEREST_EXPENSE",
  "TAX_EXPENSE",
] as const;

interface TransactionTableProps {
  selectedRows: Set<number>;
  setSelectedRows: React.Dispatch<React.SetStateAction<Set<number>>>;
  transactions: any[]; // Adjust the type according to your data structure
  mutate: () => void;
}

export const TransactionTable: React.FC<TransactionTableProps> = ({
  selectedRows,
  setSelectedRows,
  transactions,
  mutate,
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showUncategorized, setShowUncategorized] = useState(false);
  const { theme, companyId, categories, apiUrl, auth } = useAccelContext();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleRowClick = (id: number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  const getCategoryTypeById = (categoryId: number): string => {
    if (!Array.isArray(categories)) {
      return "none";
    }
    const category = categories.find((cat) => cat.id === categoryId);
    const type = category?.type || "NONE";
    const abbreviations: { [key: string]: string } = {
      ASSET: "asset",
      LIABILITY: "liab",
      EQUITY: "eqty",
      REVENUE: "rev",
      EXPENSE: "exp",
      INTEREST_INCOME: "int_inc",
      COGS_EXPENSE: "cogs_exp",
      SGA_EXPENSE: "sga_exp",
      SM_EXPENSE: "sm_exp",
      RD_EXPENSE: "rd_exp",
      INTEREST_EXPENSE: "int_exp",
      TAX_EXPENSE: "tax_exp",
      NONE: "none",
    };
    return abbreviations[type] || "none";
  };

  const isCreditCategory = (categoryId: number): boolean => {
    const creditCategories = ["REVENUE", "INTEREST_INCOME"];
    const category = categories.find((cat) => cat.id === categoryId);
    return creditCategories.includes(category?.type || "");
  };

  const isDebitCategory = (categoryId: number): boolean => {
    const debitCategories = [
      "EXPENSE",
      "COGS_EXPENSE",
      "SGA_EXPENSE",
      "SM_EXPENSE",
      "RD_EXPENSE",
      "INTEREST_EXPENSE",
      "TAX_EXPENSE",
      "LIABILITY",
    ];
    const category = categories.find((cat) => cat.id === categoryId);
    return debitCategories.includes(category?.type || "");
  };

  const handleRowSelect = (id: number) => {
    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(id)) {
      newSelectedRows.delete(id);
    } else {
      newSelectedRows.add(id);
    }
    setSelectedRows(newSelectedRows);
  };

  const handleCategoryChange = async (
    transactionId: number,
    categoryId: number
  ) => {
    await Accel.editTransaction(apiUrl, auth.access_token, {
      body: { id: transactionId, category_id: categoryId },
    }).then(() => {
      mutate();
    });
  };

  const getStatusStyles = (status: string) => {
    switch (status) {
      case "PENDING":
        return {
          color: "#FFA500",
          borderColor: "#FFA500",
          "&:hover": {
            borderColor: "#FFA500",
            backgroundColor: "rgba(255, 165, 0, 0.1)",
          },
        };
      case "CLEARED":
        return {
          color: "#00C853",
          borderColor: "#00C853",
          "&:hover": {
            borderColor: "#00C853",
            backgroundColor: "rgba(0, 200, 83, 0.1)",
          },
        };
      case "RECONCILED":
        return {
          color: "#2196F3",
          borderColor: "#2196F3",
          "&:hover": {
            borderColor: "#2196F3",
            backgroundColor: "rgba(33, 150, 243, 0.1)",
          },
        };
      default:
        return {
          color: "#9E9E9E",
          borderColor: "#9E9E9E",
          "&:hover": {
            borderColor: "#9E9E9E",
            backgroundColor: "rgba(158, 158, 158, 0.1)",
          },
        };
    }
  };

  const categoryTypeOptions = [
    { id: "ASSET", name: "Asset" },
    { id: "LIABILITY", name: "Liability" },
    { id: "EQUITY", name: "Equity" },
    { id: "REVENUE", name: "Revenue" },
    { id: "EXPENSE", name: "Expense" },
    { id: "INTEREST_INCOME", name: "Interest Income" },
    { id: "COGS_EXPENSE", name: "COGS Expense" },
    { id: "SGA_EXPENSE", name: "SGA Expense" },
    { id: "SM_EXPENSE", name: "SM Expense" },
    { id: "RD_EXPENSE", name: "R&D Expense" },
    { id: "INTEREST_EXPENSE", name: "Interest Expense" },
    { id: "TAX_EXPENSE", name: "Tax Expense" },
    { id: "NONE", name: "None" },
  ];
  const handleLedgerTypeChange = async (
    transactionId: number,
    ledgerType: string
  ) => {
    await Accel.editTransaction(apiUrl, auth.access_token, {
      body: { id: transactionId, ledger_type: ledgerType },
    }).then(() => {
      mutate();
    });
   
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (!transactions || transactions.length === 0) {
    return (
      <TableContainer
        sx={{
          backgroundColor: theme?.accent?.hex || "#000000",
          height: "100%",
          maxHeight: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress sx={{ color: theme?.accent?.hex || "#000000" }} />
      </TableContainer>
    );
  }

  const filteredTransactions = showUncategorized
    ? transactions.filter((t) => t.status === "PENDING")
    : transactions;

  const paginatedTransactions = filteredTransactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <Box sx={{ marginBottom: 0 }}>
        <SuggestionBar
          mutate={mutate}
          showUncategorized={showUncategorized}
          setShowUncategorized={setShowUncategorized}
        />
      </Box>
      <TableContainer
        sx={{
          backgroundColor: theme?.accent?.hex || "#000000",
          height: "80%",
          maxHeight: "80%",
          overflow: "auto",
          pt: "0px",
          mt: "0px",
        }}
      >
        <Table
          aria-label="transaction table"
          sx={{ width: "100%", height: "100%" }}
        >
          <TableHead>
            <TableRow sx={{ borderBottom: "none", padding: 0 }}>
              <TableCell
                sx={{ borderColor: theme?.secondary?.hex || "#000000" }}
              >
                <Typography
                  sx={{ color: theme?.text?.hex || "#000000", fontSize: 14 }}
                >
                  Select
                </Typography>
              </TableCell>
              <TableCell
                sx={{ borderColor: theme?.secondary?.hex || "#000000" }}
              >
                <Typography
                  sx={{ color: theme?.text?.hex || "#000000", fontSize: 14 }}
                >
                  Date
                </Typography>
              </TableCell>
              <TableCell
                sx={{ borderColor: theme?.secondary?.hex || "#000000" }}
              >
                <Typography
                  sx={{ color: theme?.text?.hex || "#000000", fontSize: 14 }}
                >
                  Source
                </Typography>
              </TableCell>
              <TableCell
                sx={{ borderColor: theme?.secondary?.hex || "#000000" }}
              >
                <Typography
                  sx={{ color: theme?.text?.hex || "#000000", fontSize: 14 }}
                >
                  Amount
                </Typography>
              </TableCell>
              <TableCell
                sx={{ borderColor: theme?.secondary?.hex || "#000000" }}
              >
                <Typography
                  sx={{ color: theme?.text?.hex || "#000000", fontSize: 14 }}
                >
                  Category
                </Typography>
              </TableCell>
              <TableCell
                sx={{ borderColor: theme?.secondary?.hex || "#000000" }}
              ></TableCell>
              <TableCell
                sx={{ borderColor: theme?.secondary?.hex || "#000000" }}
              ></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTransactions.map((transaction) => (
              <React.Fragment key={transaction.id}>
                <TableRow sx={{ borderBottom: "none", padding: 0 }}>
                  <TableCell
                    sx={{
                      paddingY: 1,
                      borderColor: theme?.secondary?.hex || "#000000",
                    }}
                  >
                    <Radio
                      checked={selectedRows.has(transaction.id)}
                      onChange={() => handleRowSelect(transaction.id)}
                      sx={{
                        color: selectedRows.has(transaction.id)
                          ? theme?.primary?.hex || "#000000"
                          : theme?.secondary?.hex || "#000000",
                        "&.Mui-checked": {
                          color: theme?.primary?.hex || "#000000",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      paddingY: 1,
                      borderColor: theme?.secondary?.hex || "#000000",
                    }}
                  >
                    <Typography
                      sx={{
                        color: theme?.text?.hex || "#000000",
                        fontSize: 14,
                      }}
                    >
                      {format(new Date(transaction.date), "MMM do")}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      paddingY: 1,
                      borderColor: theme?.secondary?.hex || "#000000",
                    }}
                  >
                    <Typography
                      sx={{
                        color: theme?.text?.hex || "#000000",
                        fontSize: 14,
                      }}
                    >
                      {transaction.counterparty_name}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      paddingY: 1,
                      borderColor: theme?.secondary?.hex || "#000000",
                    }}
                  >
                    <Typography
                      sx={{
                        color:
                          transaction.direction === "CREDIT"
                            ? theme?.positive?.hex || "#00C853"
                            : theme?.negative?.hex || "#F44336",
                        fontSize: 14,
                      }}
                    >
                      {`${transaction.direction === "CREDIT" ? "+" : "-"}$${transaction.amount.toFixed(2)}`}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ paddingY: 1, borderColor: "#292929" }}>
                    <Select
                      value={transaction.category_id || ""}
                      onChange={(event) => {
                        const selectedValue = event.target.value;
                        if (
                          categoryTypeOptions.some(
                            (option) => option.id === selectedValue
                          )
                        ) {
                          // Handle setting the type without a specific category
                          handleLedgerTypeChange(transaction.id, selectedValue);
                        } else {
                          handleCategoryChange(transaction.id, selectedValue);
                        }
                      }}
                      sx={{
                        color: theme?.primary?.hex || "#000000",
                        borderColor: theme?.primary?.hex || "#000000",
                        borderRadius: "6px", // Slightly increase the border radius for a softer rectangle
                        textTransform: "none",
                        fontSize: 12, // Increase the text size for better readability
                        padding: "4px 12px", // Increase padding for a larger box
                        width: "150px", // Increase the width for more content space
                        "&:hover": {
                          borderColor: theme?.primary?.hex || "#000000",
                          backgroundColor: "rgba(255, 165, 0, 0.1)",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme?.primary?.hex || "#000000",
                          borderRadius: "6px", // Match the container's borderRadius
                        },
                        "& .MuiSelect-select": {
                          paddingTop: "4px",
                          paddingBottom: "4px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis", // Add text cut-off
                        },
                        "& .MuiSvgIcon-root": {
                          color: theme?.primary?.hex || "#000000",
                          fontSize: 18, // Slightly increase the icon size
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            backgroundColor: theme?.accent?.hex || "#000000",
                            color: theme?.secondary?.hex || "#000000",
                          },
                        },
                      }}
                    >
                      {categoryTypeOptions.map((type) => (
                        <MenuItem
                          key={type.id}
                          value={type.id}
                          sx={{
                            "&:hover": {
                              backgroundColor: `${theme?.primary?.hex || "#000000"}1A`,
                            },
                          }}
                        >
                          {type.name} {/* Display category type name */}
                        </MenuItem>
                      ))}
                      {categories
                        ?.filter((category) =>
                          transaction.direction === "CREDIT"
                            ? isCreditCategory(category.id)
                            : isDebitCategory(category.id)
                        )
                        .map((category) => (
                          <MenuItem
                            key={category.id}
                            value={category.id}
                            sx={{
                              "&:hover": {
                                backgroundColor: `${theme?.primary?.hex || "#000000"}1A`,
                              },
                            }}
                          >
                            {`${category.name} - ${getCategoryTypeById(category.id)}`}{" "}
                            {/* Display category name first, then expense type */}
                          </MenuItem>
                        ))}
                    </Select>
                  </TableCell>
                  <TableCell
                    sx={{
                      paddingY: 1,
                      borderColor: theme?.secondary?.hex || "#000000",
                    }}
                  >
                    <Button
                      variant="outlined"
                      sx={{
                        borderRadius: "20px",
                        textTransform: "none",
                        ...getStatusStyles(transaction.status),
                      }}
                    >
                      {transaction.status}
                    </Button>
                  </TableCell>
                  <TableCell
                    sx={{
                      paddingY: 1,
                      borderColor: theme?.secondary?.hex || "#000000",
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => handleRowClick(transaction.id)}
                      endIcon={
                        expandedRows.has(transaction.id) ? (
                          <ArrowDropUp />
                        ) : (
                          <ArrowDropDown />
                        )
                      }
                      sx={{
                        color: theme?.primary?.hex || "#000000",
                        borderColor: theme?.primary?.hex || "#000000",
                        borderRadius: "20px",
                        textTransform: "none",
                        "&:hover": {
                          borderColor: theme?.primary?.hex || "#000000",
                          backgroundColor: `${theme?.primary?.hex || "#000000"}1A`,
                        },
                      }}
                    >
                      {expandedRows.has(transaction.id) ? "Close" : "View"}
                    </Button>
                  </TableCell>
                </TableRow>

                {expandedRows.has(transaction.id) && (
                  <TableRow sx={{ padding: 0 }}>
                    <TableCell colSpan={6} sx={{ paddingY: 0 }}>
                      <Typography
                        sx={{
                          color: theme?.text?.hex || "#000000",
                          fontSize: 14,
                        }}
                      >
                        {transaction.description}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredTransactions.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          color: theme?.text?.hex || "#000000",
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
            {
              color: theme?.text?.hex || "#000000",
            },
          "& .MuiTablePagination-select": {
            color: theme?.text?.hex || "#000000",
          },
          "& .MuiTablePagination-selectIcon": {
            color: theme?.text?.hex || "#000000",
          },
          "& .MuiIconButton-root.Mui-disabled": {
            color: `${theme?.text?.hex || "#000000"}50`,
          },
        }}
      />
    </>
  );
};

export default TransactionTable;
